<template>
  <div class="asInput" :class="{ '-error': error }">
    <label
      v-if="label"
      class="asInput_label"
      :for="id"
    >
      {{label}}
      <small v-if="note" class="asInput_note">{{ note }}</small>
      <small v-if="showCountLetter">({{`${valueLength} / ${maxlength}`}})</small>
    </label>

    <input
      v-imask="mask"
      ref="inputRef"
      class="asInput_input"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :maxlength="maxlength"
      @input="onInput"
      @accept="onAccept"
      @complete="onComplete"
      @change="onChange"
      :id="id"
      :name="name"
      @focus="onFocus"
      @blur="onBlur"
      :readonly="readonly"
      :data-cy="`input-${name}`"
    />

    <div
      v-if="appendIcon"
      class="asInput_icon"
      :class="'-' + appendIcon"
      @click="clickAppendIcon"
    ></div>
    <span v-if="showHint" class="asInput_hint" v-html="hint"/>
    <span class="asInput_error" v-html="error"/>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { IMaskDirective } from 'vue-imask'
import useInput, { props, emits } from '@web/composition/useInput'

export default defineComponent({
  name: 'AsInput',
  directives: {
    imask: IMaskDirective
  },
  props,
  emits,
  setup (props, ctx) {
    const valueLength = computed(() => props.modelValue.toString().length)
    return {
      valueLength,
      ...useInput(props, ctx.emit)
    }
  }
})
</script>

<style lang="scss">
  @import '@vars';

  .asInput {
    position: relative;

    width: 100%;
    max-width: 342px;
    margin-top: 16px;

    &.-fullWidth {
      max-width: 100% !important;
    }

    &:first-child {
      margin-top: 0;
    }

    &_label {
      display: flex;

      color: rgba(255, 255, 255, 0.7);
      font-size: 12px;
      line-height: 20px;

      small {
        margin-left: auto;
      }
    }

    &_note {
      margin-left: 5px !important;
      margin-right: auto;
    }

    &.-black &_label {
      color: rgba(0, 0, 0, 0.7);
    }

    &_input {
      display: block;
      width: 100%;
      margin-bottom: 1px;

      background-color: transparent;
      border: 0;
      outline: none;
      border-bottom: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 0;

      color: #ffffff;
      font-size: 16px;
      line-height: 22px;
      letter-spacing: 0.3px;
      caret-color: white;

      transition: color .3s;

      appearance: textfield;
      -moz-appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &.-value,
      &:not([value=""]) {
        border-bottom: 1px solid rgb(255, 255, 255);
      }

      &:focus {
        border-bottom: 2px solid #ffffff;
        margin-bottom: 0;
      }

      &:read-only {
        border-width: 1px !important;
      }

      &::-webkit-input-placeholder { /* Chrome/Opera/Safari */
        color: #fff;
      }

      &::-moz-placeholder { /* Firefox 19+ */
        color: #fff;
      }

      &:-ms-input-placeholder { /* IE 10+ */
        color: #fff;
      }

      &::placeholder {
        color: #fff;
      }

      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus {
        -webkit-text-fill-color: #ffffff;
        -webkit-box-shadow: 0 0 0 1000px transparent inset;
        transition: background-color 5000s ease-in-out 0s;
        background: transparent -webkit-linear-gradient(top,  rgba(255,255,255,0) 0%,rgba(0,174,255,0.04) 50%,rgba(255,255,255,0) 51%,rgba(0,174,255,0.03) 100%);
        font: normal 16px "Open Sans", sans-serif !important;
      }
    }

    &.-black &_input {
      color: #000;
      border-color: rgba(0, 0, 0, 0.3);

      &:focus {
        border-color: #000;
      }

      &.-value {
        border-color: #000;
      }

      &::-webkit-input-placeholder { /* Chrome/Opera/Safari */
        color: #000;
      }

      &::-moz-placeholder { /* Firefox 19+ */
        color: #000;
      }

      &:-ms-input-placeholder { /* IE 10+ */
        color: #000;
      }

      &::placeholder {
        color: #000;
      }
    }

    &.-grey &_label {
      color: rgba(0, 0, 0, 0.7);
    }

    &.-grey &_input {
      color: #4c5264;
      border-color: rgba(0, 0, 0, 0.3);
      caret-color: #4c5264;

      input {
        color: #4c5264;
      }

      &:focus {
        border-color: #4c5264;
      }

      &.-value {
        border-color: #4c5264;
      }

      &::-webkit-input-placeholder { /* Chrome/Opera/Safari */
        color: #4c5264;
      }

      &::-moz-placeholder { /* Firefox 19+ */
        color: #4c5264;
      }

      &:-ms-input-placeholder { /* IE 10+ */
        color: #4c5264;
      }

      &::placeholder {
        color: #4c5264;
      }

      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus {
        -webkit-text-fill-color: #4c5264;
      }
    }

    &_icon {
      position: absolute;
      top: 18px;
      right: 4px;

      display: block;
      height: 22px;
      width: 22px;

      background-position: center center;
      background-size: contain;
      cursor: pointer;

      &.-visibility {
        background-image: url(./assets/baseline-visibility-24px.svg);
      }

      &.-visibilityOff {
        background-image: url(./assets/baseline-visibility_off-24px.svg);
      }
    }

    &_error {
      display: inline-block;
      padding-top: 5px;

      color: red;

      &:empty {
        display: none;
      }
    }

    &.-error &_input {
      border-bottom-color: red;
    }

    &_hint {
      display: inline-block;
      padding-top: 5px;

      color: black;

      &:empty {
        display: none;
      }
    }

    &_timePicker {
      .dropdown ul li:not([disabled]).active, .vue__time-picker .dropdown ul li:not([disabled]).active:focus {
        background: $colorMain !important;
      }
    }

    &.-only-text &_timePicker {
      border-bottom: none !important;
    }

    &.-only-text &_timePicker {
      input {
        background: transparent;
        border: none;
        color: currentColor;
        text-align: right;
        cursor: pointer;

        &::placeholder {
          color: white;
        }
      }
    }

    .vdp-datepicker {
      color: black;
    }
  }

  .vdp-datepicker input {
    display: block;
    width: 100%;
    margin-bottom: 1px;

    background-color: transparent;
    border: 0;
    outline: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);

    color: #ffffff;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0.3px;

    transition: color .3s;

    &.-value,
    &:not([value=""]) {
      border-bottom: 1px solid rgb(255, 255, 255);
    }

    &:focus {
      border-bottom: 2px solid #ffffff;
      margin-bottom: 0;
    }

    &::-webkit-input-placeholder { /* Chrome/Opera/Safari */
      color: #ffffff;
    }

    &::-moz-placeholder { /* Firefox 19+ */
      color: #ffffff;
    }

    &:-ms-input-placeholder { /* IE 10+ */
      color: #ffffff;
    }

    &::placeholder {
      color: white;
    }
  }
</style>
